import { Inject, Injectable } from '@angular/core';
import { get } from 'lodash';

import { GlobalConstant } from '@constants/global.constant';
import { AlertInfos } from '@constants/partials/flight-alerts.constant';
import { deserialiseData } from 'src/app/core/helpers/mapper.helper';
import { CancelInfoDetails, FlightLeg, PassengerTypeRefund } from '../order/_interfaces_/booked-order.interface';
import { BookedOrderActionInfoMapped } from '../order/_interfaces_/booked-order.mapped.interface';
import { PassengerMapperService } from '../passenger/passenger-mapper.service';
import { ConditionEntry, TicketConditions } from './_interfaces_/booked-flight-ticket-conditions.interface';
import { BookedFlightTicketConditionMapped } from './_interfaces_/booked-flight-ticket-conditions.mapped.interface';
import { Alert, BookedFare, BookedFlight } from './_interfaces_/booked-flight.interface';
import {
  AirlineMapped,
  AirportMapped,
  BookedFareMapped,
  BookedFlightMapped,
  CancelInfoMapped,
  EditFlightLeg,
  EditFlightResponseMapped,
  FlightAlertMapped,
  LegMapped,
  PassengerTypeRefundsMapped,
  SegmentMapped,
} from './_interfaces_/booked-flight.mapped.interface';
import { Airline, Airport, Leg, Segment } from './_interfaces_/leg.interface';

@Injectable({
  providedIn: 'root',
})
export class FlightMapperService {
  public alertInfos: AlertInfos;

  constructor(
    @Inject(PassengerMapperService) private _passengerMapperService: PassengerMapperService,
    @Inject(GlobalConstant) private _globalConstant: GlobalConstant
  ) {
    this.alertInfos = this._globalConstant.flightAlerts.alertInfos;
  }

  public mapBookedFlight(flight: BookedFlight): BookedFlightMapped {
    try {
      return {
        passengers: this._passengerMapperService.mapPassengers(flight._embedded.passengers),
        displayPrice: flight.displayPrice,
        bookedFare: this.mapBookedFare(flight.bookedFare),
        pnr: flight.pnr,
        displayFareType: flight.displayFareType,
        departureAirport: flight.departureAirport,
        arrivalAirport: flight.arrivalAirport,
        departureDateTime: flight.departureDateTime,
        arrivalDateTime: flight.arrivalDateTime,
        airline: this.mapAirline(flight.airline),
        selectedLegs: this.mapLegs(flight.selectedLegs),
        alerts: this.mapAlerts(flight.alerts, flight.selectedLegs),
        textRemarks: flight.textRemarks,
        passengersWithExtrasInfo: flight.passengersWithExtrasInfo,
      };
    } catch (e) {
      console.error(e);
    }
  }

  public mapRetrivedFlight(actionInfo: BookedOrderActionInfoMapped): EditFlightResponseMapped {
    return {
      pnr: '',
      identifier: actionInfo?.flightIdentifier,
      flightLeg: this.mapRetrivedFlightLeg(actionInfo?.changeFlightDetails?.legs),
      cancelInfo: actionInfo?.cancellationPossible ? this.mapCancelInfo(actionInfo?.cancelInfoDetails) : null,
    };
  }

  public mapRetrivedFlightLeg(response: FlightLeg[]): EditFlightLeg[] {
    if (response) {
      return response.map((leg: FlightLeg) => {
        return {
          legNumber: leg?.legNumber,
          departureAirport: leg?.departureAirport,
          departureDate: leg?.departureDate,
          departureTime: leg?.departureTime,
          arrivalAirport: leg?.arrivalAirport,
          arrivalDate: leg?.arrivalDate,
          arrivalTime: leg?.arrivalTime,
          cabinClass: leg?.cabinClass,
          nonStop: leg?.nonStop,
          isFlown: leg?.isFlown,
        };
      });
    }
    return Array<FlightLeg>();
  }

  public mapCancelInfo(cancelInfoDetails: CancelInfoDetails): CancelInfoMapped {
    return {
      airtradeFees: cancelInfoDetails?.airtradeFees,
      passengerTypeRefunds: this.mapPassengerTypeRefunds(cancelInfoDetails?.passengerTypeRefunds),
      providerFees: cancelInfoDetails?.providerFees,
      totalFees: cancelInfoDetails?.totalFees,
    };
  }

  public mapPassengerTypeRefunds(passengerTypeRefunds: PassengerTypeRefund[]): PassengerTypeRefundsMapped[] {
    if (passengerTypeRefunds) {
      return passengerTypeRefunds.map((passenger: PassengerTypeRefund) => ({
        numberOfPassengers: passenger.numberOfPassengers,
        passengerType: passenger.passengerType,
        subTotalAirtradeFees: passenger.subTotalAirtradeFees,
        subTotalAirtradeFeesAverage: passenger.subTotalAirtradeFeesAverage,
        subTotalFees: passenger.subTotalFees,
        subTotalFeesAverage: passenger.subTotalFeesAverage,
        subTotalProviderFees: passenger.subTotalProviderFees,
        subTotalProviderFeesAverage: passenger.subTotalProviderFeesAverage,
      }));
    }
    return Array<PassengerTypeRefund>();
  }

  public mapBookedFare(fare: BookedFare): BookedFareMapped | null {
    if (!fare) {
      return null;
    }

    const services = fare.services.map(({ displayName }) => ({ displayName }));
    return {
      services,
      price: fare.price,
      providerFareCode: fare.providerFareCode,
      airtradeFareCode: fare.airtradeFareCode,
    };
  }

  public mapLegs(legs: Leg[]): LegMapped[] {
    return legs.map((leg: Leg) => ({
      legNumber: leg.legNumber,
      direction: leg.direction,
      departureAirport: this.mapAirport(leg.departureAirport),
      departureDate: leg.departureDate,
      departureTime: leg.departureTime,
      arrivalAirport: this.mapAirport(leg.arrivalAirport),
      arrivalDate: leg.arrivalDate,
      arrivalTime: leg.arrivalTime,
      duration: leg.duration,
      numberOfTransfers: leg.numberOfTransfers,
      baggageIndicator: leg.baggageIndicator,
      airline: this.mapAirline(leg.airline),
      segments: leg.segments.map((segment) => this.mapLegSegment(segment)),
    }));
  }

  public mapAirport(airport: Airport): AirportMapped {
    return {
      code: airport.code,
      displayName: airport.displayName,
      airportShortName: airport.airportShortName,
      displayNameShort: airport.displayNameShort,
    };
  }

  public mapAirline(airline: Airline): AirlineMapped {
    if (!airline) {
      return null;
    }

    return {
      code: airline.code,
      displayName: airline.displayName,
    };
  }

  public mapLegSegment(segment: Segment): SegmentMapped {
    return {
      departureDate: get(segment, 'departureDate', null),
      departureTime: get(segment, 'departureTime', null),
      flightNumber: segment.flightNumber,
      cabinClass: segment.cabinClass,
      distance: segment.distance,
      arrivalDate: get(segment, 'arrivalDate', null),
      arrivalTime: get(segment, 'arrivalTime', null),
      waitingTime: segment.waitingTime,
      equipment: segment.equipment,
      bookingClass: segment.bookingClass,
      hasAirportChanged: segment.hasAirportChanged,
      baggageAllowance: segment.baggageAllowance || null,
      availableChairs: segment.availableChairs,
      arrivalTerminal: segment.arrivalTerminal || null,
      airline: this.mapAirline(segment.airline),
      codeShareAirline: this.mapAirline(segment.codeShareAirline),
      arrivalAirport: this.mapAirport(segment.arrivalAirport),
      departureAirport: this.mapAirport(segment.departureAirport),
    };
  }

  public mapAlerts(alerts: Alert[], selectedLegs: Leg[]): FlightAlertMapped[] {
    if (alerts?.length === 0) {
      return [];
    }

    const alertsMapped: FlightAlertMapped[] = [];
    alerts.forEach((alert) => {
      const alertMapped = this.mapFlightAlert(alert, selectedLegs);
      if (alertMapped) {
        alertsMapped.push(alertMapped);
      }
    });
    return alertsMapped;
  }

  public mapFlightAlert(alert: Alert, selectedLegs: Leg[]): FlightAlertMapped | null {
    const { alertType: alertType, oldValue: oldValue, newValue: newValue } = alert;
    const info = this.alertInfos[alertType];

    if (!info) {
      return null;
    }

    const parentLeg: Leg = selectedLegs.find(({ legNumber }) => legNumber === Number(alert.reference));
    const segment: Segment = parentLeg ? parentLeg.segments[Number(alert.productReference.segmentIndex)] : null;
    const segmentName: string = segment ? `${segment.departureAirport.code} - ${segment.arrivalAirport.code}` : null;
    const alertMapped = { info, alertType, oldValue, newValue, segmentName };

    return alertMapped;
  }

  public mapTicketConditions({ ticketConditionList: conditions }: TicketConditions): BookedFlightTicketConditionMapped[] | null {
    if (!conditions || conditions.length === 0) {
      return null;
    }
    return conditions.map((con) => {
      const condition = { ...con, id: con.title.replace(/\s+/g, '') };
      return deserialiseData<ConditionEntry, BookedFlightTicketConditionMapped>(condition);
    });
  }
}
